import { useEffect, useState } from "react";
import { MessageSquareText } from "lucide-react";
import { ApiError, errorMessage } from "../api/client";
import type { GenericMessage, VerifySent } from "../api/types";
import { Button, CodeInput, ErrorText } from "./ui";

interface Props {
  phoneMasked: string;
  sent: boolean;
  initialError?: string | null;
  onResend: () => Promise<VerifySent>;
  onCheck: (code: string) => Promise<GenericMessage>;
  onVerified: (message: string) => void;
}

export function PhoneVerifyPanel({ phoneMasked, sent, initialError, onResend, onCheck, onVerified }: Props) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [info, setInfo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(sent ? 60 : 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (value = code) => {
    if (value.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      onVerified((await onCheck(value)).message);
    } catch (err) {
      setError(errorMessage(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(null);
    setInfo(null);
    try {
      const r = await onResend();
      setCooldown(r.resend_available_in_seconds);
      setInfo(`New code sent to ${r.phone_masked}. It expires in ${Math.round(r.expires_in_seconds / 60)} minutes.`);
    } catch (err) {
      setError(errorMessage(err));
      if (err instanceof ApiError && err.code === "RESEND_COOLDOWN") {
        const m = err.message.match(/(\d+) seconds/);
        if (m) setCooldown(Number(m[1]));
      }
    }
  };

  return (
    <div data-testid="phone-verify-panel" className="space-y-6">
      <div className="flex items-start gap-3 rounded-sm border border-white/10 bg-ink p-4">
        <MessageSquareText className="mt-0.5 h-5 w-5 shrink-0 text-signal" />
        <p data-testid="phone-verify-sent-to" className="text-sm text-zinc-200">
          {sent ? <>We texted a code to <span className="font-mono text-white">{phoneMasked}</span>. It expires in 10 minutes.</>
            : <>Send a code to <span className="font-mono text-white">{phoneMasked}</span> to verify your phone.</>}
        </p>
      </div>
      {sent && (
        <div className="space-y-3">
          <p className="label-caps text-zinc-400">6-digit code</p>
          <CodeInput value={code} onChange={setCode} onComplete={verify} testId="phone-verify-code-input" autoFocus />
        </div>
      )}
      <ErrorText message={error} testId="phone-verify-error" />
      {info && <p data-testid="phone-verify-info" className="text-xs text-emerald-300">{info}</p>}
      <div className="flex flex-wrap items-center gap-3">
        {sent && <Button loading={busy} disabled={code.length !== 6} onClick={() => verify()} data-testid="phone-verify-submit-button">Verify phone</Button>}
        <Button variant="secondary" disabled={cooldown > 0} onClick={resend} data-testid="phone-verify-resend-button">
          {cooldown > 0 ? `Resend code in ${cooldown}s` : sent ? "Resend code" : "Send code"}
        </Button>
      </div>
      <p className="text-xs text-zinc-500">Msg & data rates may apply. Reply HELP for help, STOP to opt out.</p>
    </div>
  );
}
